import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Calendar, ExternalLink, Zap, Send } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsultationForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-[#0D0D0D] border border-[#333333] p-8 lg:p-12 card-glow" 
      data-testid="consultation-form"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label 
              htmlFor="name" 
              className="text-[#F5F5F7] text-sm"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Full Name *
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Jane Smith"
              value={formData.name}
              onChange={handleChange}
              className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff]"
              required
              data-testid="form-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label 
              htmlFor="email" 
              className="text-[#F5F5F7] text-sm"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Email Address *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="jane@example.com"
              value={formData.email}
              onChange={handleChange}
              className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff]"
              required
              data-testid="form-email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label 
            htmlFor="phone" 
            className="text-[#F5F5F7] text-sm"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Phone Number
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={formData.phone}
            onChange={handleChange}
            className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff]"
            data-testid="form-phone-input"
          />
        </div>

        <div className="space-y-2">
          <Label 
            htmlFor="message" 
            className="text-[#F5F5F7] text-sm"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Tell us about yourself
          </Label>
          <Textarea
            id="message"
            name="message"
            placeholder="What interests you about court reporting? Any questions you'd like us to address?"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff] resize-none"
            data-testid="form-message-input"
          />
        </div>

        <Button
          type="submit"
          className="w-full rounded-none bg-[#38b6ff] text-[#0D0D0D] hover:bg-[#38b6ff]/90 btn-neon font-semibold py-6"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
          disabled={isLoading}
          data-testid="form-submit-button"
        >
          {isLoading ? (
            "Submitting..."
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Request Information
            </>
          )}
        </Button>
      </form>

      {/* Calendar Booking Link */}
      <div className="mt-8 pt-8 border-t border-[#333333]">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-[#E8E8E8]/10 border border-[#E8E8E8]/30 flex items-center justify-center">
            <Calendar className="w-6 h-6 text-[#E8E8E8]" />
          </div>
          <div>
            <p className="font-medium text-[#F5F5F7]">Prefer to schedule directly?</p>
            <p className="text-sm text-[#E0E0E0]">Book a Zoom consultation at your convenience</p>
          </div>
        </div>
        <a
          href="https://calendly.com/aymhighsteno/enrollment-discussion"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#38b6ff] hover:text-[#E8E8E8] font-medium text-sm transition-colors"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
          data-testid="calendar-link"
        >
          <Zap className="w-4 h-4" />
          <span>Schedule on Calendly</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </motion.div>
  );
}
